import React from "react";

import type { NotificationActionType, NotificationState } from "./reducer";
import { NotificationAction, reducer } from "./reducer";
import type { NotificationItem } from "./types";

const useNotification = <D extends NotificationItem>(
  initialStack: D[] = []
): {
  state: NotificationState<D>;
  setStack: (payload: D[]) => void;
  addStackItem: (payload: D) => void;
  removeStackItem: (key: string | number) => void;
  notify: (notification: D, duration?: number, onDismiss?: () => void) => void;
  dismissNotification: (key: string | number) => void;
} => {
  const [state, dispatch] = React.useReducer<React.Reducer<NotificationState<D>, NotificationActionType<D>>>(reducer, {
    stack: initialStack,
    notifications: [],
  });

  const setStack = (payload: D[]): void => {
    dispatch({ type: NotificationAction.SET_STACK, payload });
  };

  const addStackItem = (payload: D): void => {
    dispatch({ type: NotificationAction.ADD_STACK_ITEM, payload });
  };

  const removeStackItem = (key: string | number): void => {
    dispatch({ type: NotificationAction.REMOVE_STACK_ITEM, payload: key });
  };

  const notify = (notification: D, duration = 5000, onDismiss?: () => void) => {
    dispatch({ type: NotificationAction.NOTIFY, payload: notification });

    const timer = window.setTimeout(() => {
      dispatch({ type: NotificationAction.REMOVE_NOTIFICATION, payload: notification.key });
      if (onDismiss) onDismiss();
      window.clearTimeout(timer);
    }, duration);
  };

  const dismissNotification = (key: string | number): void => {
    dispatch({ type: NotificationAction.REMOVE_NOTIFICATION, payload: key });
  };

  return { state, setStack, addStackItem, removeStackItem, notify, dismissNotification };
};

export default useNotification;
